import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import classnames from 'classnames/bind';
import _ from 'lodash';
import * as graph from '@adc/sdk';
import WhatChangedSummarySection from './whatChangedSummarySection';
import { formatters, traditionalOffer } from '../../../../utils';
import styles from './styles.scss';
import { bidding } from '@adc/blueprint/';
const cx = classnames.bind(styles);
const ELM_IDS = {
    offerDetails: 'offer-details',
    closing: 'closing',
    contingencies: 'contingencies',
};
const valueOrDefault = (value, asNegative = false, defaultValue = 'N/A') => {
    return value !== undefined && value !== null
        ? asNegative
            ? `-${value}`
            : value
        : defaultValue;
};
const getPreviousDaysLabel = (value, complement = 'Days', defaultValue = 'Not Requested') => (value ? `${value} ${complement}` : value == 0 ? defaultValue : undefined);
const fields = bidding.enums.WhatChangedFields;
const WhatChangedSummary = ({ bidRank, viewingFrom = graph.enums.CurrentTargetAudience.Seller, listing, segment, previousValues, }) => {
    const terms = bidRank.bid?.terms;
    const { buyerCommissionsAmount, sellerCommissionsAmount, sellerConcessionAmount, } = traditionalOffer.getOfferTermsCalculations(bidRank.bid);
    let commissionPayerType = undefined;
    if (bidRank?.bid?.terms?.commission?.length) {
        commissionPayerType = bidRank?.bid?.terms?.commission[0].paid_by;
    }
    const paymentOption = _.find(traditionalOffer.paymentTypeOptions, option => option.value === terms?.payment_type);
    const previousPaymentOption = _.find(traditionalOffer.paymentTypeOptions, option => option.value === previousValues['/terms/payment_type']);
    const buyerAgentCommission = buyerCommissionsAmount + sellerCommissionsAmount;
    let sections = useMemo(() => [
        {
            title: 'Offer Details',
            className: cx('row-children'),
            children: [
                {
                    label: 'Purchase Price',
                    value: valueOrDefault(terms?.offer_amount),
                    isMoneyValue: !!terms?.offer_amount,
                    elmId: `${ELM_IDS.offerDetails}-purchase-price`,
                    previousValue: previousValues[fields.offerAmount],
                },
                {
                    label: 'Payment Type',
                    value: valueOrDefault(paymentOption?.text, false, 'N/A'),
                    elmId: `${ELM_IDS.offerDetails}-payment-type`,
                    previousValue: previousPaymentOption?.text,
                },
                {
                    label: 'Down payment',
                    value: valueOrDefault(terms?.down_payment_amount, false, `0`),
                    elmId: `${ELM_IDS.offerDetails}-down-payment`,
                    isMoneyValue: true,
                    previousValue: previousValues[fields.downPaymentAmount],
                },
                {
                    label: 'Earnest Money Deposit',
                    value: valueOrDefault(terms?.earnest_money_deposit_amount, false, `0`),
                    elmId: `${ELM_IDS.offerDetails}-earnest-money-deposit`,
                    isMoneyValue: true,
                    previousValue: previousValues[fields.earnestMoneyDepositAmount],
                },
            ],
        },
        {
            title: 'Closing',
            className: cx('row-children'),
            children: [
                {
                    label: 'Closing Date',
                    value: valueOrDefault(terms?.close_escrow_duration
                        ? `${terms?.close_escrow_duration} Days from Contract Execution`
                        : undefined),
                    elmId: `${ELM_IDS.closing}-closing-date`,
                    previousValue: getPreviousDaysLabel(previousValues[fields.closeEscrowDuration], 'Days from Contract Execution', 'N/A'),
                },
                {
                    label: 'Rentback',
                    value: valueOrDefault(terms?.leaseback_duration
                        ? `${formatters.formatMoney(terms?.leaseback_amount)} per day for ${terms?.leaseback_duration} Days`
                        : undefined, false, 'Not Offered'),
                    elmId: `${ELM_IDS.closing}-rentback`,
                    hide: !listing?.seller_terms?.leaseback_terms
                        ?.leaseback_period_in_days &&
                        !listing?.seller_terms?.leaseback_terms?.leaseback_period_rent,
                    previousValue: previousValues[fields.leasebackDuration]
                        ? `${formatters.formatMoney(previousValues[fields.leasebackAmount])} per day for ${previousValues[fields.leasebackDuration]} Days`
                        : previousValues[fields.leasebackDuration] == 0
                            ? 'Not Offered'
                            : undefined,
                },
                {
                    label: 'Seller Paid Closing Costs',
                    value: valueOrDefault(sellerConcessionAmount, false, `0`),
                    elmId: `${ELM_IDS.closing}-seller-paid-closing-costs`,
                    isMoneyValue: true,
                    previousValue: previousValues[fields.sellerConcessionAmount],
                },
                {
                    label: 'Buyer’s Agent Commission',
                    value: valueOrDefault(buyerAgentCommission, false, `0`),
                    elmId: `${ELM_IDS.closing}-appraisal-contingency`,
                    isMoneyValue: true,
                    previousValue: previousValues[fields.agentCommissionAmount],
                },
                {
                    label: 'BA Commission Paid By',
                    value: valueOrDefault(commissionPayerType),
                    elmId: `${ELM_IDS.closing}-appraisal-contingency`,
                    previousValue: previousValues[fields.paidBy],
                },
            ],
        },
        {
            title: 'Contingencies',
            className: cx('row-children'),
            children: [
                {
                    label: 'Inspection Contingency',
                    value: valueOrDefault(terms?.inspection_contingency_period_duration
                        ? `${terms?.inspection_contingency_period_duration} Days`
                        : undefined, false, 'Not Requested'),
                    elmId: `${ELM_IDS.contingencies}-inspection-contingency`,
                    previousValue: getPreviousDaysLabel(previousValues[fields.inspectionContingencyPeriodDuration]),
                    hide: !!listing?.seller_terms?.inspection_terms
                        ?.is_option_contingency,
                },
                {
                    label: 'Option Period',
                    value: valueOrDefault(terms?.option_period_duration
                        ? `${formatters.formatMoney(terms?.option_period_cost_amount)} for ${terms?.option_period_duration} Days`
                        : undefined, false, `Not Requested`),
                    elmId: `${ELM_IDS.contingencies}-option-period`,
                    previousValue: previousValues[fields.optionPeriodDuration]
                        ? `${formatters.formatMoney(previousValues[fields.optionPeriodCostAmount])} for ${getPreviousDaysLabel(previousValues[fields.optionPeriodDuration])}`
                        : previousValues[fields.optionPeriodDuration] == 0
                            ? `Not Requested`
                            : undefined,
                    hide: !listing?.seller_terms?.inspection_terms
                        ?.is_option_contingency,
                },
                {
                    label: 'Financing Contingency',
                    value: valueOrDefault(terms?.financing_contingency_duration
                        ? `${terms?.financing_contingency_duration} Days`
                        : undefined, false, 'Not Requested'),
                    elmId: `${ELM_IDS.contingencies}-financing-contingency`,
                    previousValue: getPreviousDaysLabel(previousValues[fields.financingContingencyDuration]),
                },
                {
                    label: 'Appraisal Contingency',
                    value: valueOrDefault(terms?.appraisal_contingency_duration
                        ? `${terms?.appraisal_contingency_duration} Days`
                        : undefined, false, `Not Requested`),
                    elmId: `${ELM_IDS.contingencies}-appraisal-contingency`,
                    previousValue: getPreviousDaysLabel(previousValues[fields.appraisalContingencyDuration]),
                },
            ],
        },
    ], [bidRank, listing, previousValues, segment, viewingFrom]);
    let additionalNotes = useMemo(() => {
        return {
            title: 'Additional Notes',
            className: cx('row-children'),
            children: [
                {
                    label: '',
                    value: terms?.notes?.additional_notes || '',
                    elmId: `additional-notes-field`,
                    previousValue: previousValues[fields.additionalNotes],
                },
            ],
        };
    }, [bidRank, listing, segment, viewingFrom]);
    return (_jsx("div", { className: cx('section-container'), children: _jsx("div", { className: cx('summary-section-container'), children: _jsx(WhatChangedSummarySection, { sections: sections, additionalNotes: additionalNotes }) }, 'left') }));
};
export default WhatChangedSummary;
//# sourceMappingURL=index.js.map